import { useEffect, useState } from 'react';
import { formatDateTime } from '../lib/format.js';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Stack,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  TablePagination,
  Chip,
  TextField,
  MenuItem,
  Tooltip,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Button,
  Divider,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useMe, useOrgSessions, type OrgSessionQuery } from '../api/hooks.js';
import { useActiveProject } from '../lib/activeProject.js';
import { useT } from '../i18n/useT.js';

const fmtDuration = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
};

type SortField = NonNullable<OrgSessionQuery['sortBy']>;

/** Text/number filters committed via "Apply" (kept separate from live controls). */
interface Draft {
  country: string;
  minDurationS: string;
  maxDurationS: string;
  minActions: string;
}
const EMPTY_DRAFT: Draft = { country: '', minDurationS: '', maxDurationS: '', minActions: '' };

/** Accumulates the distinct non-empty values picked from loaded rows, so a live
 * dropdown keeps a full option list even after filtering narrows the rows. */
function useAccumulated<T>(rows: readonly T[], pick: (r: T) => string | null | undefined, selected: string): string[] {
  const [opts, setOpts] = useState<string[]>([]);
  useEffect(() => {
    const seen = new Set(opts);
    let changed = false;
    for (const r of rows) {
      const v = pick(r);
      if (v && !seen.has(v)) { seen.add(v); changed = true; }
    }
    if (changed) setOpts([...seen].sort((a, b) => a.localeCompare(b)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);
  return selected && !opts.includes(selected) ? [selected, ...opts] : opts;
}

/** Org-wide session list with filtering, sorting and pagination. */
export function AllSessionsPage() {
  const t = useT().sessions;
  const { data: me } = useMe();
  const orgId = me?.organizations[0]?.id;
  const { selected: projectId, setProjectId, projects } = useActiveProject(orgId);
  const navigate = useNavigate();

  // Live controls (apply immediately).
  const [hideBots, setHideBots] = useState(true);
  const [source, setSource] = useState('');
  const [browser, setBrowser] = useState('');
  const [os, setOs] = useState('');
  const [sortBy, setSortBy] = useState<SortField>('startedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Deferred text/number filters (apply on button).
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [applied, setApplied] = useState<Draft>(EMPTY_DRAFT);
  const setD = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const query: OrgSessionQuery = {
    projectId: projectId || undefined,
    excludeBots: hideBots,
    trafficSource: source || undefined,
    browser: browser || undefined,
    os: os || undefined,
    country: applied.country || undefined,
    minDurationMs: applied.minDurationS ? Math.round(Number(applied.minDurationS) * 1000) : undefined,
    maxDurationMs: applied.maxDurationS ? Math.round(Number(applied.maxDurationS) * 1000) : undefined,
    minInteractions: applied.minActions ? Number(applied.minActions) : undefined,
    sortBy,
    sortDir,
    limit: rowsPerPage,
    offset: page * rowsPerPage,
  };
  const sessions = useOrgSessions(orgId, query);
  const rows = sessions.data?.data ?? [];
  const total = sessions.data?.total ?? 0;

  // Distinct values for the live dropdowns (accumulated across loaded pages).
  const sourceMenu = useAccumulated(rows, (r) => r.trafficSource, source);
  const browserMenu = useAccumulated(rows, (r) => r.browser, browser);
  const osMenu = useAccumulated(rows, (r) => r.os, os);

  const sort = (field: SortField) => {
    if (sortBy === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(field); setSortDir('desc'); }
    setPage(0);
  };
  const head = (field: SortField, label: string, align: 'left' | 'right' = 'left') => (
    <TableCell align={align} sortDirection={sortBy === field ? sortDir : false}>
      <TableSortLabel active={sortBy === field} direction={sortBy === field ? sortDir : 'desc'} onClick={() => sort(field)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const applyFilters = () => { setApplied(draft); setPage(0); };
  const clearFilters = () => { setDraft(EMPTY_DRAFT); setApplied(EMPTY_DRAFT); setPage(0); };
  const draftCount = Object.values(applied).filter((v) => v !== '').length;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h5">{t.title}</Typography>
          {!sessions.isLoading && <Chip size="small" label={t.total(total.toLocaleString())} />}
        </Stack>
      </Stack>

      {/* Unified filter bar. Every filter together in one place. */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
          <FilterListIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <TextField select size="small" label={t.filters.source} value={source} onChange={(e) => { setSource(e.target.value); setPage(0); }} sx={{ minWidth: 140 }}>
            <MenuItem value="">{t.filters.allSources}</MenuItem>
            {sourceMenu.map((s) => (<MenuItem key={s} value={s}>{s}</MenuItem>))}
          </TextField>
          <TextField select size="small" label={t.filters.browser} value={browser} onChange={(e) => { setBrowser(e.target.value); setPage(0); }} sx={{ minWidth: 130 }}>
            <MenuItem value="">{t.filters.allBrowsers}</MenuItem>
            {browserMenu.map((b) => (<MenuItem key={b} value={b}>{b}</MenuItem>))}
          </TextField>
          <TextField select size="small" label={t.filters.os} value={os} onChange={(e) => { setOs(e.target.value); setPage(0); }} sx={{ minWidth: 120 }}>
            <MenuItem value="">{t.filters.allOs}</MenuItem>
            {osMenu.map((o) => (<MenuItem key={o} value={o}>{o}</MenuItem>))}
          </TextField>
          <TextField select size="small" label={t.filters.project} value={projectId} onChange={(e) => { setProjectId(e.target.value); setPage(0); }} sx={{ minWidth: 160 }}>
            <MenuItem value="">{t.filters.allProjects}</MenuItem>
            {projects.map((p) => (<MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>))}
          </TextField>
          <TextField size="small" label={t.filters.location} placeholder={t.filters.country} value={draft.country} onChange={(e) => setD('country', e.target.value)} sx={{ width: 140 }} />
          <TextField size="small" type="number" label={t.filters.minDuration} value={draft.minDurationS} onChange={(e) => setD('minDurationS', e.target.value)} sx={{ width: 110 }} />
          <TextField size="small" type="number" label={t.filters.maxDuration} value={draft.maxDurationS} onChange={(e) => setD('maxDurationS', e.target.value)} sx={{ width: 110 }} />
          <TextField size="small" type="number" label={t.filters.minActions} value={draft.minActions} onChange={(e) => setD('minActions', e.target.value)} sx={{ width: 120 }} />
          <Tooltip title={t.filters.hideCrawlersHint}>
            <FormControlLabel
              sx={{ ml: 0.5 }}
              control={<Checkbox size="small" checked={hideBots} onChange={(e) => { setHideBots(e.target.checked); setPage(0); }} />}
              label={t.filters.hideCrawlers}
            />
          </Tooltip>
          <Divider orientation="vertical" flexItem />
          {draftCount > 0 && <Button size="small" onClick={clearFilters}>{t.filters.clear(draftCount)}</Button>}
          <Button size="small" variant="contained" onClick={applyFilters}>{t.filters.apply}</Button>
        </Stack>
      </Paper>

      <Paper sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t.columns.project}</TableCell>
              <TableCell>{t.columns.user}</TableCell>
              <TableCell>{t.columns.source}</TableCell>
              {head('browser', t.columns.browserOs)}
              <TableCell>{t.columns.location}</TableCell>
              {head('durationMs', t.columns.duration, 'right')}
              {head('interactionCount', t.columns.actions, 'right')}
              <TableCell>{t.columns.signals}</TableCell>
              {head('startedAt', t.columns.started)}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((s) => (
              <TableRow hover key={s.id} sx={{ cursor: 'pointer' }} onClick={() => navigate(`/projects/${s.projectId}/sessions/${s.id}`)}>
                <TableCell><Chip size="small" variant="outlined" label={s.projectName ?? '—'} sx={{ maxWidth: 150 }} /></TableCell>
                <TableCell>{s.userEmail ?? s.userId ?? s.anonymousId.slice(0, 12)}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {s.trafficSource ? <Chip size="small" variant="outlined" label={s.trafficSource} sx={{ maxWidth: 150 }} /> : <Typography variant="caption" color="text.disabled">—</Typography>}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{s.browser} / {s.os}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{[s.country, s.city].filter(Boolean).join(' · ') || '—'}</TableCell>
                <TableCell align="right">{fmtDuration(s.durationMs)}</TableCell>
                <TableCell align="right">
                  <Tooltip title={t.actionsHint}>
                    <span>{(s.interactionCount ?? 0).toLocaleString()}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    {s.hasError && <Chip label={t.signals.error} color="error" size="small" />}
                    {s.hasRageClick && <Chip label={t.signals.rage} color="warning" size="small" />}
                    {s.hasDeadClick && <Chip label={t.signals.dead} size="small" />}
                    {s.isBounce && <Chip label={t.signals.bounce} size="small" variant="outlined" />}
                    {s.isBot && <Chip label={t.signals.bot} size="small" color="default" variant="outlined" sx={{ opacity: 0.7 }} />}
                  </Stack>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(s.startedAt)}</TableCell>
              </TableRow>
            ))}
            {sessions.isLoading && (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={26} /></TableCell></TableRow>
            )}
            {!sessions.isLoading && rows.length === 0 && (
              <TableRow><TableCell colSpan={9}><Typography color="text.secondary" sx={{ py: 2 }}>{t.empty}</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </Paper>
    </Box>
  );
}
